import { type RecoveryFlow, ResponseError } from '@ory/client-fetch';
import { type OryClientConfiguration, OryLocales } from '@ory/elements-react';
import { Recovery } from '@ory/elements-react/theme';
import { createFileRoute, useRouter } from '@tanstack/react-router';
import { useEffect, useMemo, useState } from 'react';
import { z } from 'zod';

import { oryComponentOverrides } from '@apps/web/components/auth/ory_components';
import { useOry } from '@apps/web/hooks/ory';

// Kratos message ids meaning "the link you clicked is dead", as opposed to an
// error about something the person typed. New customers are onboarded with a
// single-use admin recovery link, so re-clicking a spent setup link (or having
// a mail scanner burn it first) is a routine arrival here, not an edge case.
// 4060006 ("recovery code is invalid or has already been used") is
// deliberately excluded: it describes a code the person just entered, and it
// has to stay visible on the form.
// https://www.ory.sh/docs/kratos/concepts/ui-messages
const SPENT_RECOVERY_LINK_MESSAGE_IDS = [
    4060004, // The recovery token is invalid or has already been used.
    4060005, // The recovery flow expired.
];

// Kratos attaches the dead-link error to a brand new, still-usable recovery
// flow, so everyone who lands here can continue with a fresh code. Both a new
// customer finishing setup and someone genuinely resetting a password reach
// this state, so the copy stays true for either without claiming to know which
// one it is.
const SPENT_RECOVERY_LINK_TITLE = 'Finish setting up your account';
const SPENT_RECOVERY_LINK_SUBTITLE =
    'That link was already used or has expired. Enter your email and we’ll ' +
    'send you a fresh one-time code.';

// Ory's IntlProvider merges custom translations into its exported locale table
// and leaves them there for the rest of the session, so reframing the copy on
// one visit would keep the reframed wording on every later recovery render.
// Snapshot the stock strings before anything renders and restate them every
// render, so a healthy visit puts the standard copy back.
const DEFAULT_ORY_ENGLISH_MESSAGES = { ...OryLocales['en'] };

const alreadySignedInErrorSchema = z.object({
    error: z.object({ id: z.literal('session_already_available') }),
});

export const Route = createFileRoute('/_auth/reset-password')({
    component: ResetPasswordPage,
    validateSearch: z.object({
        flow: z.string().optional(),
    }),
});

function isSpentRecoveryLinkFlow(flow: RecoveryFlow): boolean {
    return (flow.ui.messages ?? []).some((message) =>
        SPENT_RECOVERY_LINK_MESSAGE_IDS.includes(message.id),
    );
}

/**
 * Kratos guards the recovery endpoints with `IsNotAuthenticated` and answers
 * 400 `session_already_available` when the caller already has a session. That
 * is not a rare race: someone who re-clicks a spent setup link from a tab
 * where they are already signed in hits it every time, and they do not need
 * account recovery at all.
 */
async function isAlreadySignedInError(error: unknown): Promise<boolean> {
    if (!(error instanceof ResponseError) || error.response.status !== 400) {
        return false;
    }
    return await error.response
        .json()
        .then(
            (body: unknown) =>
                alreadySignedInErrorSchema.safeParse(body).success,
        )
        // A 400 from this endpoint is documented to mean exactly this, so an
        // unreadable body still points the same way.
        .catch(() => true);
}

export function ResetPasswordPage() {
    const { flow: flowId } = Route.useSearch();
    const router = useRouter();
    const [flow, setFlow] = useState<RecoveryFlow | null>(null);
    const [flowError, setFlowError] = useState<string | null>(null);
    const [isSpentLinkRecovery, setIsSpentLinkRecovery] = useState(false);
    const { oryClient, oryElementsConfiguration, normalizeOryPayload } =
        useOry();

    useEffect(() => {
        // Nothing in here may leave the page rendering `null` forever: the
        // effect only re-runs when the flow id changes, so a swallowed failure
        // is a permanent blank page rather than a transient one.
        async function handleFlowFailure(
            error: unknown,
            fallbackFlow: RecoveryFlow | null,
        ) {
            if (await isAlreadySignedInError(error)) {
                router.navigate({ to: '/home', replace: true });
                return;
            }
            console.error(error);
            if (fallbackFlow) {
                setFlow(fallbackFlow);
                return;
            }
            setFlowError(
                'Unable to load password reset right now. Please try again.',
            );
        }

        (async () => {
            setFlowError(null);

            if (!flowId) {
                try {
                    const createdFlow = normalizeOryPayload(
                        await oryClient.createBrowserRecoveryFlow(),
                    );
                    router.navigate({
                        to: '/reset-password',
                        search: (prev) => ({ ...prev, flow: createdFlow.id }),
                        replace: true,
                    });
                } catch (error: unknown) {
                    await handleFlowFailure(error, null);
                }
                return;
            }

            let existingFlow: RecoveryFlow;
            try {
                existingFlow = normalizeOryPayload(
                    await oryClient.getRecoveryFlow({
                        id: flowId,
                    }),
                );
            } catch (error: unknown) {
                await handleFlowFailure(error, null);
                return;
            }

            if (!isSpentRecoveryLinkFlow(existingFlow)) {
                setFlow(existingFlow);
                return;
            }

            // Swap the flow carrying the dead-link error for a clean one, so
            // the page shows an actionable code form instead of a raw Kratos
            // error the customer cannot do anything about. `replace` keeps the
            // spent flow id out of history: going Back to it would re-run this
            // detection and mint another flow on every press.
            try {
                const replacementFlow = normalizeOryPayload(
                    await oryClient.createBrowserRecoveryFlow(),
                );
                setIsSpentLinkRecovery(true);
                router.navigate({
                    to: '/reset-password',
                    search: (prev) => ({ ...prev, flow: replacementFlow.id }),
                    replace: true,
                });
            } catch (error: unknown) {
                // Degraded but usable: the flow Kratos already handed us still
                // renders the email form, just with its raw error attached.
                await handleFlowFailure(error, existingFlow);
            }
        })();
    }, [flowId, normalizeOryPayload, oryClient, router]);

    useEffect(() => {
        if (flow?.state === 'passed_challenge') {
            router.navigate({ to: '/sign-in' });
        }
    }, [flow, router]);

    const recoveryConfiguration = useMemo((): OryClientConfiguration => {
        const { intl } = oryElementsConfiguration;
        return {
            ...oryElementsConfiguration,
            intl: {
                locale: intl?.locale ?? 'en',
                customTranslations: {
                    ...intl?.customTranslations,
                    en: {
                        ...DEFAULT_ORY_ENGLISH_MESSAGES,
                        ...intl?.customTranslations?.['en'],
                        ...(isSpentLinkRecovery
                            ? {
                                  'recovery.title': SPENT_RECOVERY_LINK_TITLE,
                                  'recovery.subtitle':
                                      SPENT_RECOVERY_LINK_SUBTITLE,
                              }
                            : {}),
                    },
                },
            },
        };
    }, [isSpentLinkRecovery, oryElementsConfiguration]);

    if (!flow) {
        if (flowError) {
            return (
                <div className="mx-auto max-w-xl px-6 py-12 text-center">
                    <h1 className="text-xl font-semibold">
                        Password reset unavailable
                    </h1>
                    <p className="text-muted-foreground mt-3 text-sm">
                        {flowError}
                    </p>
                </div>
            );
        }

        return null;
    }

    return (
        <div className="ory-root w-full max-w-xl">
            <Recovery
                flow={flow}
                config={recoveryConfiguration}
                components={oryComponentOverrides}
            />
        </div>
    );
}
